import {
  CreateCheckoutRequest,
  CheckoutProfile,
  User,
  CartEntry,
  Cart as SubCart,
  CartBundleSlot,
  getValueCheck,
  SopMetadata,
  Price,
  CheckoutProfileService,
  checkoutApi,
  CheckoutApiResponse,
  CheckoutResponse,
  PaymentDetail,
} from '@commerce-checkout-webcomponents/sdk';
import { isEmpty } from 'lodash';
import { Cart, CartEntryDetails, SopElementResponse, SubCartDetail } from '@commerce-cart-webcomponents/sdk';
import { FiredPromotion } from '@commerce-checkout-webcomponents/sdk/services/models/FiredPromotion';
import { StorageService } from '@nextgen-web-framework/core';
import { AdvanceShareCodeResponse } from '@commerce-webcomponents/sdk';
import { findMainCart } from '../transformer';
import { DataTypesEnums, LocalStorageKeys } from '../config/checkoutConstants';

// TODO: Remove as once cart and checkout models are consistent and same
const transformCartEntryToCheckoutEntry = (cartEntry: CartEntryDetails): CartEntry => ({
  quantity: cartEntry.quantity,
  isFreeItem: cartEntry?.isFreeItem,
  isPersistent: cartEntry?.isPersistent,
  entryId: Number(cartEntry?.entryId),
  partnerPromotionId: cartEntry?.partnerPromotionId,
  sopElementId: cartEntry?.sopElementId,
  product: {
    id: cartEntry.product.id,
    derivedAvailabilityStatus: cartEntry.product?.derivedAvailabilityStatus,
    itemDisposition: cartEntry.product?.itemDisposition,
    isAuxiliary: cartEntry.product?.isAuxiliary,
  },
  promotionResults: {
    firedPromotions: cartEntry?.promotionResults?.firedPromotions as unknown as Array<FiredPromotion>,
  },
  bundleSlots: cartEntry.bundleSlots as unknown as Array<CartBundleSlot>,
  auxiliaryEntryIds: cartEntry.auxiliaryEntryIds ?? [],
  qualifyingItemIds: cartEntry.qualifyingEntries ?? [],
});

const getCheckoutRequestUser = (amwayUser: User): User => {
  const user: User = {
    id: amwayUser?.id,
    accountId: amwayUser?.accountId,
    firstName: amwayUser?.firstName,
    lastName: amwayUser?.lastName,
    volumeAbo: amwayUser?.volumeAbo,
    mobileNo: amwayUser?.mobileNo,
    countryCode: amwayUser?.countryCode,
    email: amwayUser?.email,
    accountType: amwayUser?.accountType,
  };
  return user;
};

export const transformCartSopDataToCheckoutSopData = (cartSopData?: Array<SopElementResponse>) => {
  let checkoutSopData = [] as Array<SopMetadata>;
  if (cartSopData?.length) {
    checkoutSopData = cartSopData.map((sopMetaObj: SopElementResponse) => ({
      sopElementId: sopMetaObj?.sopElementId,
      sopId: sopMetaObj?.sopId,
      sopCode: sopMetaObj?.sopCode,
      sopPlanId: sopMetaObj?.sopPlanId,
      planName: sopMetaObj?.planName,
      images: sopMetaObj?.images,
      elementSequence: sopMetaObj?.elementSequence,
      totalShipments: sopMetaObj?.totalShipments,
      isCompensated: sopMetaObj?.isCompensated,
      isPostponed: sopMetaObj?.isPostponed,
      coupons: sopMetaObj?.coupons,
      promotionStacking: sopMetaObj?.promotionStacking,
      purchaseByDate: sopMetaObj?.purchaseByDate,
      isFreeDelivery: sopMetaObj?.isFreeDelivery,
      //TODO: Need to fix models from cart and checkout
      version: sopMetaObj?.version,
      ...(sopMetaObj?.totalDiscount && {
        totalDiscount: sopMetaObj?.totalDiscount as unknown as Price,
      }),
      ...(sopMetaObj?.promotionResults && {
        promotionResults: {
          firedPromotions: sopMetaObj?.promotionResults.firedPromotions as unknown as Array<FiredPromotion>,
        },
      }),
    }));
  }
  return checkoutSopData;
};

export const getPaymentDetails = (checkoutProfile?: CheckoutProfile, mainCart?: SubCartDetail) => {
  let paymentDetails: PaymentDetail[] = [];
  if (mainCart?.paymentDetails?.length) {
    paymentDetails = mainCart?.paymentDetails.reduce((paymentDetailsList: PaymentDetail[], paymentDetail) => {
      if (!isEmpty(paymentDetail)) {
        paymentDetailsList.push({
          paymentMethodCode: paymentDetail?.paymentMethodCode || checkoutProfile?.payment?.paymentMethodId,
          paymentPlanId: paymentDetail?.paymentPlanId,
        });
      }
      return paymentDetailsList;
    }, []);
  }
  if (!mainCart?.paymentDetails?.length && checkoutProfile?.payment?.paymentMethodId) {
    paymentDetails = [{ paymentMethodCode: checkoutProfile?.payment?.paymentMethodId }];
  }
  return paymentDetails;
};

export const transformCartDataToCheckoutRequest = (
  cart: Cart,
  profile: User,
  referralCode?: string,
  checkoutProfile?: CheckoutProfile,
  cartSelectionFlag?: boolean,
): CreateCheckoutRequest => {
  const mainCart = findMainCart(cart?.subCarts || [], cart.id);
  const checkoutRequest: CreateCheckoutRequest = {
    cartId: cart?.id,
    user: getCheckoutRequestUser(profile),
    referralCode: referralCode,
    promotionResults: {
      firedPromotions: mainCart?.promotionResults?.firedPromotions as unknown as Array<FiredPromotion>,
    },
    appliedCoupons: mainCart?.couponResults?.appliedCoupons,
    entries:
      ((!cartSelectionFlag || (cartSelectionFlag && mainCart?.selected)) &&
        mainCart?.entries?.map((cartEntry) => transformCartEntryToCheckoutEntry(cartEntry))) ||
      [],
    subCarts: [],
    sopMetadata: transformCartSopDataToCheckoutSopData(mainCart?.sopMetaData),
    accountProcess: cart?.accountProcess,
    paymentDetails: getPaymentDetails(checkoutProfile, mainCart),
    ...(checkoutProfile?.fulfillmentType?.id && {
      fulfillmentDetail: {
        id: checkoutProfile?.fulfillmentType?.id,
        serviceLevel: checkoutProfile?.fulfillmentType?.serviceLevel?.deliveryOptionId,
        shippingAddress: checkoutProfile?.fulfillmentType?.serviceLevel?.deliveryAddress,
        serviceLocation: checkoutProfile?.fulfillmentType?.serviceLocation,
        warehouseId: checkoutProfile?.fulfillmentType?.warehouse,
      },
    }),
  };
  cart.subCarts?.length &&
    cart.subCarts.map((subCart: SubCartDetail) => {
      (!cartSelectionFlag || (cartSelectionFlag && subCart?.selected)) &&
        subCart?.subCartId != mainCart?.subCartId &&
        checkoutRequest.subCarts?.push(
          transformSubCartDataToCheckoutRequest(subCart, {
            ...subCart?.amwayUser,
            accountId: getValueCheck(subCart?.amwayUser?.accountId, DataTypesEnums.TYPE_STRING),
            id: getValueCheck(subCart?.amwayUser?.amwayUserId, DataTypesEnums.TYPE_STRING),
            ...(subCart?.amwayUser?.accountType && {
              accountType: getValueCheck(subCart?.amwayUser?.accountType, DataTypesEnums.TYPE_STRING),
            }),
          }),
        );
    });
  return checkoutRequest;
};

export const transformSubCartDataToCheckoutRequest = (subCart: SubCartDetail, profile: User): SubCart => {
  const subCartCheckoutRequest: SubCart = {
    cartId: subCart?.subCartId,
    user: getCheckoutRequestUser(profile),
    promotionResults: {
      firedPromotions: subCart.promotionResults?.firedPromotions as unknown as Array<FiredPromotion>,
    },
    appliedCoupons: subCart?.couponResults?.appliedCoupons,
    entries: subCart.entries?.map((cartEntry) => transformCartEntryToCheckoutEntry(cartEntry)) || [],
    sopMetadata: transformCartSopDataToCheckoutSopData(subCart?.sopMetaData),
  };
  return subCartCheckoutRequest;
};

export const setCheckoutIdInLocalStorage = (checkoutResponse: CheckoutResponse) => {
  const localStorage = StorageService.getLocalStorage();
  return checkoutResponse?.checkoutId
    ? localStorage.store(LocalStorageKeys.CHECKOUT_ID, checkoutResponse?.checkoutId)
    : new Error();
};

export const createCheckout = async (cart: Cart): Promise<CheckoutResponse> => {
  try {
    const localStorage = StorageService.getLocalStorage();
    const shareCodeAttributes = localStorage.retrieve(
      LocalStorageKeys.SHARECODE_ATTRIBUTES,
    ) as unknown as AdvanceShareCodeResponse;
    const profile = { ...cart?.amwayUser, id: cart.amwayUser.amwayUserId ?? '' };
    const payload = transformCartDataToCheckoutRequest(
      cart,
      profile as User,
      shareCodeAttributes?.accountId,
      CheckoutProfileService.getCheckoutProfile(),
    );
    const checkoutResp: CheckoutApiResponse = await checkoutApi.createCheckout({
      payload,
    });
    setCheckoutIdInLocalStorage(checkoutResp as CheckoutResponse);
    return checkoutResp as CheckoutResponse;
  } catch (err) {
    return Promise.reject(err);
  }
};
